/**
 * SignalR connection manager.
 *
 * Establishes a persistent WebSocket connection to the backend hub and wires
 * incoming events to the Zustand user store.
 *
 * Events handled:
 *  - Dashboard  → full initial payload (user + balances + tickets + notifications)
 *  - MasterData → reference data (currencies, statuses, etc.)
 *  - Balance    → single balance update
 *  - User       → partial user update
 *  - Noti       → new notification(s)
 *
 * Ported from V1 with the same reconnection logic.
 */

import * as signalR from '@microsoft/signalr'
import type { AuthSession } from '@/auth/AuthContext'
import { useUserStore, extractConfirmedTicketIds } from '@/store/user-store'
import { config } from '@/lib/config'
import { resolveClientFeature } from '@/lib/feature/resolveClientFeature'
import { FEATURE_QUERY_PARAM } from '@/lib/feature/feature.constants'
import { getStoredSession } from '@/auth/tokenStorage'

// ---------------------------------------------------------------------------
// Module-level state (singleton connection)
// ---------------------------------------------------------------------------

let connection:          signalR.HubConnection | null = null
let reconnectionAttempts = 0
const MAX_RECONNECTS     = 10
let isManualDisconnect   = false

// Serializa llamadas concurrentes a startConnection (p. ej. React StrictMode
// invoca el efecto de montaje dos veces): sin esto, la segunda llamada podía
// llamar stop() sobre la conexión de la primera mientras esta todavía estaba
// a mitad de handshake, y el cliente de SignalR lo rechaza con
// "Failed to start the HttpConnection before stop() was called".
let connectQueue: Promise<void> = Promise.resolve()

// ---------------------------------------------------------------------------
// Logging del payload completo del evento `Dashboard` (la carga inicial
// post-login) — un objeto expandible para poder inspeccionar cualquier
// campo sin tener que agregar un log nuevo cada vez.
// ---------------------------------------------------------------------------

function logEmittedEvent(eventName: string): void {
  console.log(`::::::::::::::::::::::::::::\nevent: ${eventName}\n::::::::::::::::::::::::::::`)
}

function logDashboardPayload(rawPayload: unknown): void {
  try {
    // El mensaje puede llegar como string JSON (mismo caso que setUserData maneja).
    const parsed = typeof rawPayload === 'string' ? JSON.parse(rawPayload) : rawPayload
    // eslint-disable-next-line no-console
    console.log('Payload crudo completo:', parsed)
  } catch (e) {
    console.error('[SignalR] Dashboard payload log error:', e)
  }
}

// ---------------------------------------------------------------------------
// Raw Noti pub/sub — consumers can subscribe to raw SignalR Noti messages
// ---------------------------------------------------------------------------

const rawNotiSubscribers = new Set<(msg: unknown) => void>()

/** Subscribe to raw SignalR Noti messages. Returns an unsubscribe function. */
export function subscribeRawNoti(cb: (msg: unknown) => void): () => void {
  rawNotiSubscribers.add(cb)
  return () => rawNotiSubscribers.delete(cb)
}

const rawBalanceSubscribers = new Set<(msg: unknown) => void>()

/** Subscribe to raw SignalR Balance messages. Returns an unsubscribe function. */
export function subscribeRawBalance(cb: (msg: unknown) => void): () => void {
  rawBalanceSubscribers.add(cb)
  return () => rawBalanceSubscribers.delete(cb)
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function startConnection(session: AuthSession): Promise<void> {
  const run = connectQueue.then(() => startConnectionInternal(session))
  // No propagar el rechazo a la cadena — si una llamada falla, la siguiente
  // igual debe poder intentar conectar.
  connectQueue = run.catch(() => {})
  return run
}

async function startConnectionInternal(session: AuthSession): Promise<void> {
  const {
    setUserData,
    setMasterData,
    setIsLoadedData,
    setIsConnecting,
    getUser,
    updateBalanceData,
    updateUserData,
    updateNotifications,
  } = useUserStore.getState()

  if (!config.signalrUrl) {
    console.warn('[SignalR] VITE_SIGNALR_URL is not configured — connection skipped.')
    return
  }

  const { id } = getUser()
  const token = session.bearerToken
  // El token puede rotar en background (refresh silencioso) mientras la
  // conexión sigue viva — accessTokenFactory se re-evalúa en cada
  // (re)negociación, así que lee siempre el más reciente de storage en vez
  // de cerrar sobre el `token` capturado al conectar.

  // Ensure any existing connection is fully stopped before creating a new one.
  // Without this await, a logout-triggered stopConnection() (fire-and-forget) may
  // still be mid-close when re-login calls startConnection(), causing the guard
  // to see state === Connected and return early — Dashboard never fires.
  if (connection) {
    await stopConnection()
  }

  try {
    setIsConnecting(true)
    isManualDisconnect = false

    const feature = resolveClientFeature()
    const hubUrl = feature
      ? `${config.signalrUrl}?${FEATURE_QUERY_PARAM}=${feature}`
      : config.signalrUrl

    connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        skipNegotiation:    true,
        transport:          signalR.HttpTransportType.WebSockets,
        accessTokenFactory: () => getStoredSession()?.bearerToken ?? token,
        timeout:            30_000,
      })
      .withAutomaticReconnect([0, 2_000, 5_000, 10_000, 15_000, 30_000])
      .configureLogging(signalR.LogLevel.Warning)
      .build()

    // Dashboard — always register so re-login after any logout path works correctly.
    // Show loading skeleton only on fresh connect (store empty = id === 0).
    if (id === 0) setIsLoadedData(true)
    connection.on('Dashboard', message => {
      try {
        logEmittedEvent('Dashboard')
        if (message == null) { return }
        logDashboardPayload(message)
        setUserData(message)
        setIsLoadedData(false)
        reconnectionAttempts = 0
      } catch (e) {
        console.error('[SignalR] Dashboard handler error:', e)
      }
    })

    connection.on('MasterData', message => {
      try {
        logEmittedEvent('MasterData')
        setMasterData(message)
      }
      catch (e) { console.error('[SignalR] MasterData error:', e) }
    })

    connection.on('Balance', message => {
      try {
        logEmittedEvent('Balance')
        // El hub a veces manda este evento como string JSON sin parsear en
        // vez de un objeto ya deserializado. Parseamos UNA sola vez acá para
        // que todos los consumidores (updateBalanceData, rawBalanceSubscribers,
        // extractConfirmedTicketIds) reciban siempre un objeto consistente —
        // antes cada uno duplicaba (o directamente omitía) este chequeo.
        const parsed = typeof message === 'string' ? JSON.parse(message) : message
        console.log('[SignalR] Balance : ', parsed)
        updateBalanceData(parsed)
        // Dispatch to raw subscribers (e.g. send-money conciliation)
        rawBalanceSubscribers.forEach(cb => {
          try { cb(parsed) } catch (e) { console.error('[SignalR] rawBalance subscriber error:', e) }
        })
        // Solo saca de pendingTransfers los tickets que este evento confirma
        // (TicketStatusId 4) — no todos, para no borrar otras transferencias
        // que sigan esperando su propia confirmación.
        const confirmedIds = extractConfirmedTicketIds(parsed)
        if (confirmedIds.length > 0) useUserStore.getState().resolvePendingTransfers(confirmedIds)
      }
      catch (e) { console.error('[SignalR] Balance error:', e) }
    })

    connection.on('User', message => {
      try {
        logEmittedEvent('User')
        updateUserData(message)
      }
      catch (e) { console.error('[SignalR] User error:', e) }
    })

    connection.on('Noti', message => {
      try {
        logEmittedEvent('Noti')
        updateNotifications(message)
        // Broadcast to raw subscribers (e.g. send-money conciliation)
        rawNotiSubscribers.forEach(cb => {
          try { cb(message) } catch (e) { console.error('[SignalR] rawNoti subscriber error:', e) }
        })
        // Any Noti means the backend processed a transfer → clear the pending banner.
        useUserStore.getState().clearPendingTransfers()
      }
      catch (e) { console.error('[SignalR] Noti error:', e) }
    })

    // Connection lifecycle events
    connection.onclose(async error => {
      setIsConnecting(false)
      if (isManualDisconnect) { connection = null; return }

      if (reconnectionAttempts < MAX_RECONNECTS) {
        reconnectionAttempts++
        const delay = Math.min(1_000 * Math.pow(2, reconnectionAttempts), 30_000)
        setTimeout(async () => {
          connection = null
          try { await startConnection(session) }
          catch (e) { console.error('[SignalR] Reconnect error:', e) }
        }, delay)
      } else {
        console.error('[SignalR] Max reconnection attempts reached.', error)
        connection = null
        setIsLoadedData(false)
      }
    })

    connection.onreconnecting(() => {
      setIsConnecting(false)
      setIsLoadedData(true)
    })

    connection.onreconnected(() => {
      setIsConnecting(true)
      setIsLoadedData(false)
      reconnectionAttempts = 0
    })

    await connection.start()
    setIsConnecting(true)
    reconnectionAttempts = 0
  } catch (err) {
    setIsConnecting(false)
    console.error('[SignalR] Connection error:', err)

    if (reconnectionAttempts < MAX_RECONNECTS && !isManualDisconnect) {
      reconnectionAttempts++
      const delay = Math.min(3_000 * reconnectionAttempts, 30_000)
      setTimeout(() => {
        connection = null
        startConnection(session).catch(console.error)
      }, delay)
    } else {
      connection = null
    }
  }
}

export async function stopConnection(): Promise<void> {
  if (!connection) return
  try {
    isManualDisconnect = true
    await connection.stop()
  } catch (e) {
    console.error('[SignalR] Stop error:', e)
  } finally {
    connection          = null
    reconnectionAttempts = 0
  }
}
